import React, { useContext, useEffect, useState, FC } from 'react';

import { CSSTransition } from 'react-transition-group';

import { GlobalContext } from '../../../../../store';
import { IGame, IGroupEnrollment } from '../../../../../store/player/games/store';
import { IEventItem } from '../../../../../store/player/events/store';

import { Title } from './Title';
import { useSortedGroupGames } from '../../Utils/useSortedGroupEnrollments';
import HoleScoresButtons from './HoleScoresButtons';
import { IDraft } from '../Utils/IDraft';
import { groupBy } from '../../../../../contrib/utils';
import HoleScorerTeam from './HoleScorerTeam';
import HoleScorerIndividual from './HoleScorerIndividual';
import useGetHoleRange from '../../Utils/getHoleRange';
import { useShotgunGameMetadata } from '../../../ClubHome/CurrentEvent/Components/CurrentEventTeeTimeParings/useShotgunGameMetadata';
import { goToNextHole } from './goToNextHole';

interface IGroupTeams {
  [teamId: number]: IGroupEnrollment[];
}

interface IProps {
  eventId: number;
  game: IGame;
  holeIndex: number;
  onWithdrawToggle: () => void;
  roundId: number;
  simpleScorecardsIsVisible: boolean;
  toggleScorecardsDrawerOpen: () => void;
  withdrawMode: boolean;
}

const HoleScorers: FC<IProps> = props => {
  const {
    eventId,
    game,
    holeIndex,
    onWithdrawToggle,
    roundId,
    simpleScorecardsIsVisible,
    toggleScorecardsDrawerOpen,
    withdrawMode
  } = props;

  const {
    actions: {
      scores: { get: getScores, save: saveScores },
      roundResults: { get: getRoundResults },
      notifications: { pushError, pushSuccess }
    },
    events,
    scores,
    user
  } = useContext(GlobalContext);

  const [, groupGames] = useSortedGroupGames(game);

  const [editMode, setEditMode] = useState(true);
  const [draftScores, setDraftScores] = useState<IDraft>({});
  const [event, setEvent] = useState<IEventItem>();
  const [allowScramble, setAllowScramble] = useState(false);
  const [groupTeams, setGroupTeams] = useState<IGroupTeams>({});

  const holesRange = useGetHoleRange(eventId, roundId);
  const { isShotgun, startingHole } = useShotgunGameMetadata(game);

  const hole =
    game && game.currentTee && game.currentTee.holes.find(h => h.order === holeIndex - 1);

  const isOfficialScorer = game && user.id === game.group.scorer;

  useEffect(() => {
    if (eventId && events) {
      const foundEvent = events[Number(eventId)];

      if (foundEvent) {
        setEvent(foundEvent);
        setAllowScramble(foundEvent.allowScramble);
      }
    }
  }, [eventId, events]);

  useEffect(() => {
    setEditMode(isOfficialScorer);
  }, [isOfficialScorer]);

  useEffect(() => {
    const groupScores = scores[game.group.id];

    if (game && hole && groupScores) {
      const draft = groupScores.reduce((acc: IDraft, s) => {
        const holeScore = s.scores.find(h => h.game === s.id && h.order === hole.order);

        return {
          ...acc,
          [s.enrollment]: {
            ...(holeScore && holeScore.id ? { id: holeScore.id } : {}),
            empty: !holeScore || !holeScore.original,
            game: s.id,
            gross: holeScore ? holeScore.gross : hole.par,
            netScore: holeScore ? holeScore.net : hole.par,
            notSaved: false,
            order: hole.order,
            original: holeScore ? holeScore.original : hole.par
          }
        };
      }, {});
      setDraftScores(draft);
    }
  }, [scores]);

  const setScore = (enrollmentId: number) => (value: number) => {
    setDraftScores({
      ...draftScores,
      [enrollmentId]: {
        ...draftScores[enrollmentId],
        notSaved: true,
        original: value
      }
    });
  };

  useEffect(() => {
    if (allowScramble) {
      setGroupTeams(groupBy(groupGames.map(groupGame => groupGame.enrollment), 'team'));
    }
  }, [groupGames, event]);

  const setScrambleScore = (teamId: number) => (value: number) => {
    setDraftScores(
      groupTeams[teamId].reduce(
        (draft, enrollment) => ({
          ...draft,
          [enrollment.id]: {
            ...draftScores[enrollment.id],
            notSaved: true,
            original: value
          }
        }),
        draftScores
      )
    );
  };

  const onSave = async () => {
    if (hole) {
      try {
        await saveScores(game.group.id, draftScores);

        await getRoundResults(roundId, eventId, true);

        // this is a quick fix, you should go to store/player/scores/actions and fix the method save
        // to append the new scores
        await getScores(game.group.id);

        pushSuccess('Scores saved successfully');

        goToNextHole({
          eventId,
          holeIndex,
          holesRange,
          isShotgun,
          roundId,
          startingHole
        });
      } catch (error) {
        pushError('Scores could not be saved, please try again');
      }
    }
  };

  return (
    <CSSTransition
      in={!withdrawMode && !simpleScorecardsIsVisible}
      timeout={500}
      classNames="fade"
      unmountOnExit={true}
    >
      <div>
        <Title toggleScorecardsDrawerOpen={toggleScorecardsDrawerOpen} />

        <div className="scorers">
          {allowScramble
            ? Object.values(groupTeams).map((teamEnrollments: IGroupEnrollment[]) => (
                <HoleScorerTeam
                  enrollments={teamEnrollments}
                  editMode={editMode}
                  draftScores={draftScores}
                  setScore={setScrambleScore}
                  hole={hole}
                />
              ))
            : game &&
              groupGames.map(({ enrollment, seat }) => (
                <HoleScorerIndividual
                  draftScores={draftScores}
                  editMode={editMode}
                  enrollment={enrollment}
                  event={event}
                  groupGames={groupGames}
                  hole={hole}
                  seat={seat}
                  setScore={setScore}
                />
              ))}
        </div>

        <HoleScoresButtons onSave={onSave} onWithdrawToggle={onWithdrawToggle} />
      </div>
    </CSSTransition>
  );
};

export default HoleScorers;
